import type {
  ApiResponse,
  FeedbackResponse,
  PaginatedResponse,
  FeedbackFilters,
} from "@snaptics/api/src/types";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    cookieHeader?: string
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    // If cookie header is provided (server-side), use it
    if (cookieHeader) {
      headers.cookie = cookieHeader;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: cookieHeader ? undefined : "include", // Include session cookies only on client-side
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: { message: response.statusText },
      }));
      throw new Error(error.error?.message || "API request failed");
    }

    const data: ApiResponse<T> = await response.json();

    if (!data.success) {
      throw new Error(data.error.message);
    }

    return data.data;
  }

  async getFeedbacks(
    filters: FeedbackFilters = {},
    cookieHeader?: string
  ): Promise<PaginatedResponse<FeedbackResponse>> {
    const params = new URLSearchParams();

    if (filters.status) params.append("status", filters.status);
    if (filters.type) params.append("type", filters.type);
    if (filters.project_id) params.append("project_id", filters.project_id);
    if (filters.limit !== undefined)
      params.append("limit", filters.limit.toString());
    if (filters.offset !== undefined)
      params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    return this.request<PaginatedResponse<FeedbackResponse>>(
      `/api/v1/feedbacks${query}`,
      {},
      cookieHeader
    );
  }

  async getFeedback(id: string, cookieHeader?: string): Promise<FeedbackResponse> {
    return this.request<FeedbackResponse>(`/api/v1/feedbacks/${id}`, {}, cookieHeader);
  }
}

export const apiClient = new ApiClient(API_URL);

/**
 * Helper to get the correct screenshot URL for both local dev and production
 */
export function getScreenshotUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;

  // If it's already a full URL (production R2), return as-is
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // Local dev: Construct the API assets endpoint
  return `${API_URL}/api/v1/assets${url}`;
}
